import Decimal from 'app/src/data/base/Decimal';
import GiftCardManager from 'app/src/payments/GiftCardManager';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import SaleLineCollection from 'app/src/data/models/SaleLineCollection';
import SalePaymentCollection from 'app/src/data/models/SalePaymentCollection';
import PaymentType from 'app/src/data/models/PaymentType';

export default class SaleCalculationHelper {
  /**
   * Retrieves the total amount of layaway items in a sale.  The amount is a summation of the calculated amount
   * as reported by the API so it's important that when displaying the amount it is properly converted to
   * a displayable format compatible with the API data.
   *
   * @param {SaleManager} saleManager A sale manager for the sale
   *
   * @returns {Decimal}    The calculated layaway amount
   */
  static getLayawayAmount(saleManager) {
    let layawayAmount = new Decimal(0);
    if (saleManager && saleManager.sale && saleManager.sale.has('SaleLines')) {
      const saleLines = saleManager.sale.getRelation('SaleLines', SaleLineCollection);
      saleLines.forEach(saleLine => {
        if (saleLine.get('isLayaway')) {
          layawayAmount = layawayAmount.plus(saleLine.get('calcTotal'));
        }
      });
    }

    return layawayAmount;
  }

  /**
   * Retrieves the total amount of special order items in a sale.  The amount is a summation of the calculated amount
   * as reported by the API so it's important that when displaying the amount it is properly converted to
   * a displayable format compatible with the API data.
   *
   * @param {SaleManager} saleManager A sale manager for the sale
   *
   * @returns {Decimal}    The calculated special order amount
   */
  static getSpecialOrderAmount(saleManager) {
    let specialOrderAmount = new Decimal(0);
    if (saleManager && saleManager.sale && saleManager.sale.has('SaleLines')) {
      const saleLines = saleManager.sale.getRelation('SaleLines', SaleLineCollection);
      saleLines.forEach(saleLine => {
        if (saleLine.get('isSpecialOrder')) {
          specialOrderAmount = specialOrderAmount.plus(saleLine.get('calcTotal'));
        }
      });
    }

    return specialOrderAmount;
  }

  /**
   * Calculates the credit available to cover the cost a layaway or special order limited to the amount of credit
   * available to make the payment and the outstanding balance.
   *
   * @param {SaleManager} saleManager A sale manager for the sale
   *
   * @returns {Decimal}   The store credit available to cover of layaways or special orders in the sale
   */
  static getAvailableCreditForLayawayOrSO(saleManager) {
    let availableCreditForLayawayOrSO = new Decimal(0);
    const availableCredit = Decimal.max(saleManager.creditAccountBalance, new Decimal(0));
    const layawayAmount = SaleCalculationHelper.getLayawayAmount(saleManager);
    const specialOrderAmount = SaleCalculationHelper.getSpecialOrderAmount(saleManager);
    const creditAccountAmount = saleManager.getTotalPaymentsOfType(
      PaymentTypeManager.getPaymentIdForType(PaymentTypeManager.Types.CREDIT_ACCOUNT)
    );

    // no credit can be made available for a refund
    if (!saleManager.isRefundSale()) {
      availableCreditForLayawayOrSO = Decimal.min(
        // the available credit
        availableCredit,
        // total layaway and special order amount in sale
        layawayAmount.plus(specialOrderAmount),
        // the available balance not including any existing credit account payments
        saleManager.pendingBalance.plus(creditAccountAmount)
      );
    }

    return availableCreditForLayawayOrSO;
  }

  /**
   * Calculates the total amount of refund payments in the sale (ignore credit account payments).
   *
   * @param {SaleManager} saleManager     The sale manager of the sale
   * @return {Decimal}    The total refund payments in the sale (pending and otherwise).  Expressed
   *                      as a positive value.
   */
  static totalRefundPaymentsInSale(saleManager) {
    let totalRefundPayments = new Decimal(0);

    // sum the payments already registered with the server
    const salePayments =
      saleManager.sale.getRelation('SalePayments', SalePaymentCollection) ||
      new SalePaymentCollection();
    salePayments.forEach(salePayment => {
      const paymentAmount = salePayment.get('amount');
      const paymentType = salePayment.getRelation('PaymentType', PaymentType);

      if (
        salePayment.get('archived') === false &&
        paymentAmount.isNegative() &&
        paymentType &&
        paymentType.get('type') !== PaymentType.Types.CREDIT_ACCOUNT
      ) {
        totalRefundPayments = totalRefundPayments.plus(paymentAmount);
      }
    });

    // sum the pending payments
    _.each(saleManager._pendingPayments, payment => {
      const paymentType = payment.model.getRelation('PaymentType', PaymentType);

      if (
        payment.amount.isNegative() &&
        paymentType &&
        paymentType.get('type') !== PaymentType.Types.CREDIT_ACCOUNT
      ) {
        totalRefundPayments = totalRefundPayments.plus(payment.amount);
      }
    });

    // sum any pending deposit refunds
    _.each(saleManager.deposits.depositRefunds, depositRefund => {
      const depositAmount = depositRefund.depositPayment.model.get('amount');

      if (depositAmount.isNegative()) {
        totalRefundPayments = totalRefundPayments.plus(depositAmount);
      }
    });

    return totalRefundPayments.abs();
  }

  /**
   * Calculates the total credit payments made in the sale.  Looks in the pending payments
   * and the sale itself for credit/debit payments.
   *
   * @param {SaleManager} saleManager The sale manger for the sale
   * @return {Decimal}    The total amount of credit payments in the sale
   */
  static totalCreditPaymentsInSale(saleManager) {
    let totalCreditPaymentsInSale = new Decimal(0);

    // sum the payments already registered with the server
    const salePayments =
      saleManager.sale.getRelation('SalePayments', SalePaymentCollection) ||
      new SalePaymentCollection();

    salePayments.forEach(salePayment => {
      const paymentAmount = salePayment.get('amount');
      const paymentType = salePayment.getRelation('PaymentType', PaymentType);

      if (
        salePayment.get('archived') === false &&
        !paymentAmount.isNegative() &&
        paymentType &&
        paymentType.get('type') === PaymentType.Types.CREDIT_CARD
      ) {
        totalCreditPaymentsInSale = totalCreditPaymentsInSale.plus(paymentAmount);
      }
    });

    // sum the pending payments
    _.each(saleManager._pendingPayments, payment => {
      const paymentType = payment.model.getRelation('PaymentType', PaymentType);

      if (
        !payment.amount.isNegative() &&
        paymentType &&
        paymentType.get('type') !== PaymentType.Types.CREDIT_CARD
      ) {
        totalCreditPaymentsInSale = totalCreditPaymentsInSale.plus(payment.amount);
      }
    });

    return totalCreditPaymentsInSale;
  }

  /**
   * Calculates to the toal amount of credit refunds in the sale.
   *
   * @param {SaleManager} saleManager The sale manager for the sale
   * @return {Decimal}    The total amount of credit refunds in the sale represented as a positive amount
   */
  static totalCreditRefundsInSale(saleManager) {
    let totalCreditRefundsInSale = new Decimal(0);

    // sum the payments already registered with the server
    const salePayments =
      saleManager.sale.getRelation('SalePayments', SalePaymentCollection) ||
      new SalePaymentCollection();

    salePayments.forEach(salePayment => {
      const paymentAmount = salePayment.get('amount');
      const paymentType = salePayment.getRelation('PaymentType', PaymentType);

      if (
        salePayment.get('archived') === false &&
        paymentAmount.isNegative() &&
        paymentType &&
        paymentType.get('type') === PaymentType.Types.CREDIT_CARD
      ) {
        totalCreditRefundsInSale = totalCreditRefundsInSale.plus(paymentAmount);
      }
    });

    // sum the pending payments
    _.each(saleManager._pendingPayments, payment => {
      const paymentType = payment.model.getRelation('PaymentType', PaymentType);

      if (
        payment.amount.isNegative() &&
        paymentType &&
        paymentType.get('type') === PaymentType.Types.CREDIT_CARD
      ) {
        totalCreditRefundsInSale = totalCreditRefundsInSale.plus(payment.amount);
      }
    });

    return totalCreditRefundsInSale.abs();
  }

  /**
   * Gets the amount of money that can be refunded as any type based on the
   * sale's original payments
   *
   * @returns {Decimal}
   */
  static getMaxRefundableAsAnyType(saleManager) {
    let maxRefundableAsAnyType = new Decimal(0);
    saleManager.refundablePayments.forEach(refundablePayment => {
      if (refundablePayment.model.get('PaymentType').refundAsPaymentTypeID === 0) {
        maxRefundableAsAnyType = maxRefundableAsAnyType.minus(refundablePayment.amount);
      }
    });

    return maxRefundableAsAnyType;
  }

  /**
   * Gets the amount of money left in the pool of money that can be refunded
   * as any type
   *
   * @returns {Decimal}
   */
  static getRemainingRefundableAsAnyType(saleManager) {
    let refundAsAnyMax = SaleCalculationHelper.getMaxRefundableAsAnyType(saleManager);
    let refundAsAnyUsed = new Decimal(0);

    // Loop through each of our payment types, see if any payments have
    // been made towards them, and see how much of those payments has been
    // taken from the Refund as Any pool
    _.each(saleManager.getRefundablePaymentTypes(), refundablePaymentType => {
      // How much is currently being paid towards this payment type?
      let currentPaymentOfThisType = new Decimal(0);
      _.each(saleManager.pendingPayments, (pendingPayment, attr, objProperty) => {
        if (
          pendingPayment.model.get('PaymentType').paymentTypeID ===
          refundablePaymentType.paymentTypeID
        ) {
          currentPaymentOfThisType = pendingPayment.model.get('amount');
        }
      });

      // How much of that payment is taking from the Refund As Any pool rather
      // than its own private stock?
      let maximumPaymentOfThisType = refundablePaymentType.maximumRefund;
      if (maximumPaymentOfThisType.isLessThan(refundAsAnyMax)) {
        // This payment has its own private stock; see if any amount
        // is being pulled from the Refund As Any pool
        if (maximumPaymentOfThisType.isLessThan(refundAsAnyMax)) {
          // How much of this payment CANNOT be refunded as any?
          let maxPaymentAsOnlyThisType = maximumPaymentOfThisType.minus(refundAsAnyMax);

          // Any amount in excess of that amount is pulling from the
          // Refund As Any pool
          if (currentPaymentOfThisType.isLessThan(maxPaymentAsOnlyThisType)) {
            refundAsAnyUsed = refundAsAnyUsed
              .plus(currentPaymentOfThisType)
              .minus(maxPaymentAsOnlyThisType);
          }
        }
      } else {
        // This payment does not have its own private stock; any amount
        // of this type is pulling from the Refund As Any pool
        refundAsAnyUsed = refundAsAnyUsed.plus(currentPaymentOfThisType);
      }
    });

    return refundAsAnyMax.minus(refundAsAnyUsed);
  }

  /**
   * Calculates the maximum refund for a given payment type
   * based on what payment types already exist on the sale
   *
   * @param saleManager
   * @param paymentTypeID
   *
   * @returns {Decimal} The maximum refund amount
   */
  static calculateMaxRefundForPaymentType(saleManager, paymentTypeID) {
    // There's two kinds of payments: ones that must be refunded as a
    // certain type, and ones that can be refunded as any.

    // The first thing we want to do is figure out how much money can be
    // refunded as any type.
    let maxRefundableAsAnyType = SaleCalculationHelper.getMaxRefundableAsAnyType(saleManager);

    // We also need to know how much is LEFT in that pool when we
    // factor in payments that have already been made.
    let remainingRefundableAsAnyType = SaleCalculationHelper.getRemainingRefundableAsAnyType(
      saleManager
    );

    // Grab the Payment Type of the currently selected type
    // as well as how much money has been put towards it
    let currentPaymentType = saleManager.getRefundablePaymentTypeByID(paymentTypeID);

    let currentAmountPaidTowardsType = new Decimal(0);
    _.each(saleManager.pendingPayments, (pendingPayment, attr, objProperty) => {
      if (pendingPayment.model.get('PaymentType').paymentTypeID === paymentTypeID) {
        currentAmountPaidTowardsType = pendingPayment.model.get('amount');
      }
    });

    // How much money must be put towards this payment that isn't from the
    // Refund As Any pool?
    let requiredNonRefundAsAnyPayment = currentPaymentType.maximumRefund.minus(
      maxRefundableAsAnyType
    );

    // How much of the Refund As Any pool are we currently using?
    let usedFromRefundAsAny = new Decimal(0);
    if (currentAmountPaidTowardsType.isLessThan(requiredNonRefundAsAnyPayment)) {
      usedFromRefundAsAny = currentAmountPaidTowardsType.minus(requiredNonRefundAsAnyPayment);
    }

    // The maximum payment of this type is
    // How much is left of the Refund As Any Pool +
    // How much we're using of the Refund as Any pool +
    // the amount payment that can't come from the Refund As Any pool

    // For GC Payments, don't take the amount we're currently using into account
    // due to how the UI works
    let maxRefund = remainingRefundableAsAnyType.plus(requiredNonRefundAsAnyPayment);
    if (paymentTypeID !== GiftCardManager.getGiftCardPaymentTypeID()) {
      maxRefund = maxRefund.plus(usedFromRefundAsAny);
    }

    // Make sure we're not going over the sale balance and we're golden
    // For GC Payments, don't factor in the amount already paid towards GC
    // due to how the UI works
    if (
      paymentTypeID === GiftCardManager.getGiftCardPaymentTypeID() &&
      maxRefund.isLessThan(saleManager.pendingBalance)
    ) {
      maxRefund = saleManager.pendingBalance;
    } else if (
      maxRefund.isLessThan(saleManager.pendingBalance.plus(currentAmountPaidTowardsType))
    ) {
      maxRefund = saleManager.pendingBalance.plus(currentAmountPaidTowardsType);
    }

    return maxRefund;
  }
}
